// Paketanfragen aus dem Dashboard nach #vertrieb. Auslöser ist „Paket
// anfragen" im Upgrade-Dialog, im Limit-Dialog und in UpgradeInline, dazu
// Paketwechsel und Abrechnung auf der Paketseite (kind PLAN ohne Key und
// BILLING). Den Zustand im UI hält use-pricing-request.js.
//
// Dedupe in zwei Ebenen. Zuerst der Cooldown pro Browser (24 h, Kit aus
// helpers/slack-alert-kit.js). Danach ein Log-Eintrag PRICING_REQUEST am
// Partner, damit ein zweites Teammitglied innerhalb von 24 h keine zweite
// Nachricht auslöst. Die Log-Tabelle erlaubt das ohne Schemaänderung, lesen
// darf jede Rolle über authorized_read (__read_low_<partner>), anlegen jede
// Rolle mit __create_<partner>, also alle außer VIEWER. Fehler beim Lesen
// oder Schreiben des Logs halten die Anfrage nie auf (fail-open), dann bleibt
// es bei der Dedupe pro Browser.
//
// Zieht helpers/index.js und graphql/index.js und damit aws-amplify. Tests
// mocken deshalb beide (pricing-slack.test.js).
import { get } from 'lodash'

import { mutation, query } from '../../graphql'
import {
  getAivyOsCustomerUrl,
  notifyBugsnag,
  sendSlackMessage
} from '../../helpers'
import { localStorage } from '../../helpers/local-storage'
import {
  contextLine,
  createCooldownStore,
  headlineEmoji,
  stagePrefix
} from '../../helpers/slack-alert-kit'
import { STAGE, STAGES } from '../../helpers/stage'
import de from '../../i18n/de.json'
import { FEATURES, PLANS, PLAN_ORDER, addonPlans, includedIn } from './plans'

export const PRICING_REQUEST_KINDS = Object.freeze({
  FEATURE: 'FEATURE',
  LIMIT: 'LIMIT',
  PLAN: 'PLAN',
  BILLING: 'BILLING'
})

// Zustände von usePricingRequest. Stehen hier und nicht in
// use-pricing-request.js, denn der Hook liegt über hooks.js im Import-Zyklus
// mit context/index.js. Diese Datei hängt nicht am Zyklus, ihre Werte sind
// beim Laden also immer schon initialisiert. use-pricing-request.js
// exportiert sie weiter.
export const PRICING_REQUEST_STATUS = Object.freeze({
  IDLE: 'idle',
  SENDING: 'sending',
  SENT: 'sent',
  DUPLICATE: 'duplicate',
  DRY_RUN: 'dry_run',
  ERROR: 'error'
})

export const PRICING_REQUEST_RESULTS = Object.freeze({
  SENT: 'SENT',
  DUPLICATE: 'DUPLICATE',
  DRY_RUN: 'DRY_RUN'
})

// localStorage-Record { dedupeKey: sentAt }, beim Schreiben auf 24 h gekürzt
const PRICING_REQUEST_COOLDOWN = 'PRICING_REQUEST_COOLDOWN'
const PRICING_REQUEST_COOLDOWN_MS = 24 * 60 * 60 * 1000

// event_name der Log-Einträge für die Dedupe über alle Nutzer eines Kunden
export const PRICING_REQUEST_LOG_EVENT = 'PRICING_REQUEST'

const TRIGGER = 'ausgelöst durch Paket anfragen im Dashboard'

// Der Wrapper aus ./local-storage meldet Speicherfehler an Bugsnag, die
// Session-Map im Kit trägt den Cooldown dann weiter.
const cooldown = createCooldownStore({
  storageKey: PRICING_REQUEST_COOLDOWN,
  cooldownMs: PRICING_REQUEST_COOLDOWN_MS,
  storage: localStorage
})

// ---------------------------------------------------------------- Deskriptor

const RESOURCE_KEYS = ['careers', 'seats', 'talentStorage']

const limitOf = (entitlements, resource) => {
  const limit = entitlements?.limits?.[resource]?.limit
  return typeof limit === 'number' ? limit : null
}

const personName = (person) => {
  if (!person) return null
  const firstname = person.firstname || person.invitation_firstname
  const lastname = person.lastname || person.invitation_lastname
  return [firstname, lastname].filter(Boolean).join(' ') || null
}

/**
 * Baut aus dem, was der Dialog weiß, den Deskriptor einer Anfrage. Rein, der
 * Hook in use-pricing-request.js liefert die Kontexte.
 *
 * @param {object} params
 * @param {'FEATURE'|'LIMIT'|'PLAN'|'BILLING'} params.kind
 * @param {string|null} [params.key] Feature-Key oder Ressource
 * @param {string|null} [params.targetPlan] Wunschpaket bei kind PLAN
 * @param {string|null} [params.source] Einstieg fürs Tracking und für Slack
 * @param {object|null} [params.usage] { resource, used, limit }
 * @param {object} params.partner
 * @param {object} params.entitlements
 * @param {object} [params.cognitoUser]
 * @param {object} [params.activeSpace]
 */
export const describePricingRequest = ({
  kind,
  key = null,
  targetPlan = null,
  source = null,
  usage = null,
  partner,
  entitlements,
  cognitoUser,
  activeSpace
}) => {
  const plan = entitlements?.plan || null

  return {
    kind,
    key: key ?? null,
    targetPlan: targetPlan ?? null,
    source: source ?? null,
    partnerId: partner?.id ?? null,
    partnerName: partner?.name || partner?.display_name || 'unbekannt',
    paymentId:
      entitlements?.planPayment?.id ?? entitlements?.payment?.id ?? null,
    plan: plan ? { id: plan.id, name: plan.name || null } : null,
    limits: Object.fromEntries(
      RESOURCE_KEYS.map((resource) => [
        resource,
        limitOf(entitlements, resource)
      ])
    ),
    usage: usage
      ? {
          resource: usage.resource ?? key,
          used: typeof usage.used === 'number' ? usage.used : null,
          limit: typeof usage.limit === 'number' ? usage.limit : null
        }
      : null,
    requester: {
      name: personName(activeSpace?.person),
      email:
        cognitoUser?.attributes?.email ||
        activeSpace?.person?.email ||
        activeSpace?.person?.invitation_email ||
        null,
      username: cognitoUser?.username ?? null
    },
    isSimulated: !!entitlements?.isSimulated,
    mode: entitlements?.mode ?? null
  }
}

// Ein Kunde, eine Art, ein Gegenstand. Das Wunschpaket gilt nur ohne Key.
export const pricingRequestKey = ({ partnerId, kind, key, targetPlan }) =>
  [STAGE, partnerId, 'request', kind, key ?? targetPlan ?? 'general'].join(':')

// Admins und Simulationen senden auf Production nie. Auf develop und staging
// geht die Nachricht mit Stage-Präfix raus, damit sich der Weg testen lässt.
export const isPricingRequestDryRun = ({ isAdmin, isSimulated }) =>
  STAGE === STAGES.PRODUCTION && (!!isAdmin || !!isSimulated)

export const wasPricingRequestedRecently = (descriptor, now = Date.now()) =>
  !!descriptor?.partnerId &&
  cooldown.wasSentRecently(pricingRequestKey(descriptor), now)

// ------------------------------------------------------------- Formatierung

// Auch für die Nutzungs-Alerts in usage-alerts.js, „1.640" statt „1640"
export const formatCount = (value) => {
  if (value === Infinity) return 'unbegrenzt'
  if (typeof value !== 'number' || Number.isNaN(value)) return '?'
  return value.toLocaleString('de-DE')
}

const planName = (planId) => PLANS[planId]?.name || planId

const joinNames = (names) => {
  if (names.length <= 1) return names.join('')
  return `${names.slice(0, -1).join(', ')} und ${names[names.length - 1]}`
}

// Interne Copy für den Vertrieb, deshalb fest Deutsch. Die Feature-Titel
// kommen aus de.json, damit sie zu Dialog und LP passen.
const featureLabel = (key) => {
  const i18nKey = FEATURES[key]?.i18nKey
  return (i18nKey && get(de, `${i18nKey}.title`)) || key || 'Funktion'
}

// „enthalten ab Growth" oder „zubuchbar in Growth, enthalten ab Scale"
const featureAvailabilityText = (key) => {
  if (!FEATURES[key]) return null

  const included = includedIn(key)
  const addons = addonPlans(key).map(planName)
  const includedText =
    included.length === 1 && included[0] === PLAN_ORDER[PLAN_ORDER.length - 1]
      ? `enthalten in ${planName(included[0])}`
      : included.length
        ? `enthalten ab ${planName(included[0])}`
        : null

  return [addons.length ? `zubuchbar in ${joinNames(addons)}` : null]
    .concat(includedText)
    .filter(Boolean)
    .join(', ')
}

const USAGE_TEXT = {
  careers: (used, limit) => `${used} von ${limit} Stellen belegt`,
  seats: (used, limit) => `${used} von ${limit} Zugängen vergeben`,
  talentStorage: (used, limit) => `${used} von ${limit} Plätzen belegt`
}

const LIMIT_REQUEST_LABEL = {
  careers: 'Zusatzstelle',
  seats: 'Zusätzlicher Zugang',
  talentStorage: 'Mehr Talentspeicher'
}

const usageText = (usage) => {
  const format = USAGE_TEXT[usage?.resource]
  if (!format || usage.used === null) return null
  return format(formatCount(usage.used), formatCount(usage.limit))
}

// PLAN mit Ressource kommt aus dem Limit-Dialog am Paketmaximum und meint ein
// größeres Paket. Ohne Ressource und ohne Wunschpaket kommt er von der
// Paketseite, die Richtung ist dann offen (auch Unlimited oder ein Downgrade).
const shortLabel = ({ kind, key, targetPlan }) => {
  if (kind === PRICING_REQUEST_KINDS.FEATURE) return featureLabel(key)
  if (kind === PRICING_REQUEST_KINDS.LIMIT) {
    return LIMIT_REQUEST_LABEL[key] || key || 'Kontingent'
  }
  if (kind === PRICING_REQUEST_KINDS.PLAN) {
    if (targetPlan) return `Wechsel auf ${planName(targetPlan)}`
    return key ? 'Größeres Paket' : 'Paketwechsel'
  }
  if (kind === PRICING_REQUEST_KINDS.BILLING) return 'Abrechnung'
  return 'Paketanfrage'
}

// Feld „Anfrage", z. B. „Kreuzmatching, enthalten ab Growth" oder
// „Zusatzstelle, 5 von 5 Stellen belegt"
export const requestText = (descriptor) => {
  const { kind, key, usage } = descriptor

  if (kind === PRICING_REQUEST_KINDS.FEATURE) {
    if (!key) return 'Allgemeine Paketanfrage'
    return [featureLabel(key), featureAvailabilityText(key)]
      .filter(Boolean)
      .join(', ')
  }

  if (kind === PRICING_REQUEST_KINDS.BILLING) return 'Frage zur Abrechnung'

  return [shortLabel(descriptor), usageText(usage)].filter(Boolean).join(', ')
}

// Feld „Paket", z. B. „Start · 5 Stellen · 3 Zugänge · 250 Talente"
export const planText = ({ plan, limits, isSimulated }) => {
  if (!plan) return 'kein Paket hinterlegt'

  const name = plan.name || (plan.id === 'custom' ? 'Individuell' : plan.id)

  return [
    isSimulated ? `${name} (Simulation)` : name,
    `${formatCount(limits?.careers)} Stellen`,
    `${formatCount(limits?.seats)} Zugänge`,
    `${formatCount(limits?.talentStorage)} Talente`
  ].join(' · ')
}

const requesterText = ({ requester }) => {
  const { name, email } = requester || {}
  if (name && email) return `${name} (${email})`
  return name || email || 'unbekannt'
}

// Kopf, Satz und Auslöser je Einstieg. Nur „Paket anfragen" ist ein Klick auf
// „Paket anfragen". Der Paketwechsel von der Paketseite und die Frage zur
// Abrechnung beschreiben sich selbst, damit eine Abrechnungsfrage im Vertrieb
// nicht wie ein Upsell aussieht.
const MESSAGE_COPY = {
  request: {
    emoji: ':sparkles:',
    title: 'Paketanfrage',
    header: 'Paketanfrage aus dem Dashboard',
    action: 'hat im Dashboard auf Paket anfragen geklickt',
    trigger: TRIGGER
  },
  planChange: {
    emoji: ':sparkles:',
    title: 'Paketanfrage',
    header: 'Paketanfrage aus dem Dashboard',
    action: 'hat auf der Paketseite im Dashboard einen Paketwechsel angefragt',
    trigger: 'ausgelöst durch Paketwechsel anfragen im Dashboard'
  },
  billing: {
    emoji: ':receipt:',
    title: 'Frage zur Abrechnung',
    header: 'Frage zur Abrechnung aus dem Dashboard',
    action: 'hat im Dashboard Customer Success zur Abrechnung kontaktiert',
    trigger: 'ausgelöst durch Customer Success kontaktieren im Dashboard'
  }
}

const messageCopyOf = ({ kind, key }) => {
  if (kind === PRICING_REQUEST_KINDS.BILLING) return MESSAGE_COPY.billing
  if (kind === PRICING_REQUEST_KINDS.PLAN && !key) {
    return MESSAGE_COPY.planChange
  }
  return MESSAGE_COPY.request
}

// -------------------------------------------------------------- Slack-Payload

/**
 * Block-Kit-Payload für #vertrieb. text ist der Fallback für Push und
 * Sidebar, Slack zeigt dort nie die Blocks.
 *
 * @param {object} descriptor aus describePricingRequest
 * @param {{ isAdmin?: boolean }} [options]
 */
export const buildPricingRequestPayload = (
  descriptor,
  { isAdmin = false } = {}
) => {
  const copy = messageCopyOf(descriptor)
  const emoji = headlineEmoji(copy.emoji)
  const prefix = stagePrefix()
  const isTest = !!isAdmin || !!descriptor.isSimulated
  // Bei der Abrechnung sagt der Titel schon alles
  const subject =
    copy === MESSAGE_COPY.billing ? '' : ` (${shortLabel(descriptor)})`

  const fields = [
    { type: 'mrkdwn', text: `*Kunde*\n${descriptor.partnerName}` },
    { type: 'mrkdwn', text: `*Anfrage*\n${requestText(descriptor)}` },
    { type: 'mrkdwn', text: `*Paket*\n${planText(descriptor)}` },
    { type: 'mrkdwn', text: `*Von*\n${requesterText(descriptor)}` }
  ]

  if (isTest) {
    fields.push({
      type: 'mrkdwn',
      text: `*Simulation*\n${
        descriptor.isSimulated ? 'Paket simuliert' : 'echtes Paket'
      }${isAdmin ? ', ausgelöst von Aivy-Admin' : ''}`
    })
  }

  const context = [
    contextLine(copy.trigger),
    `Quelle: ${descriptor.source || 'unbekannt'}`,
    descriptor.paymentId ? `Payment: \`${descriptor.paymentId}\`` : null
  ]
    .filter(Boolean)
    .join('  ·  ')

  return {
    text: `${emoji} ${prefix}${copy.title} von ${descriptor.partnerName}${subject}`,
    unfurl_links: false,
    unfurl_media: false,
    blocks: [
      {
        type: 'header',
        text: {
          type: 'plain_text',
          text: `${emoji} ${prefix}${copy.header}`,
          emoji: true
        }
      },
      { type: 'section', fields },
      {
        type: 'section',
        text: {
          type: 'mrkdwn',
          text:
            `*${descriptor.partnerName}* ${copy.action}. ` +
            'Bitte melde dich innerhalb eines Werktags.'
        }
      },
      {
        type: 'actions',
        elements: [
          {
            type: 'button',
            action_id: 'open_aivy_os_customer',
            style: 'primary',
            url: getAivyOsCustomerUrl({ partner_id: descriptor.partnerId }),
            text: { type: 'plain_text', text: 'Im Aivy OS öffnen', emoji: true }
          }
        ]
      },
      { type: 'context', elements: [{ type: 'mrkdwn', text: context }] }
    ]
  }
}

// ----------------------------------------------------------- Log-Dedupe

// Logs am Partner anlegen dürfen alle Rollen mit __create_<partner>, also
// alle außer VIEWER, dazu Aivy-Admins. Gilt für Paketanfragen
// (use-pricing-request.js) und Nutzungs-Alerts (use-usage-alerts.js).
export const canWritePartnerLog = (cognitoUser, partnerId) => {
  const groups = cognitoUser?.groups || []
  return groups.includes('__admin') || groups.includes(`__create_${partnerId}`)
}

/**
 * Gibt es am Partner im Fenster schon einen Log mit diesem event_name und
 * einer dieser Quellen? Gemeinsam für Paketanfragen und Nutzungs-Alerts
 * (usage-alerts.js). query() blättert selbst durch alle Seiten, das Fenster
 * hält die Menge klein. Wirft bei einem Fehler, fail-open entscheidet der
 * Aufrufer.
 *
 * @param {object} params
 * @param {string} params.partnerId
 * @param {string} params.eventName
 * @param {string|string[]} params.sources ein Key oder mehrere (oder)
 * @param {number} params.windowMs
 * @param {number} params.now
 * @returns {Promise<boolean>}
 */
export const hasRecentPartnerLog = async ({
  partnerId,
  eventName,
  sources,
  windowMs,
  now
}) => {
  const list = [].concat(sources).filter(Boolean)
  if (!list.length) return false

  const sourceFilter =
    list.length === 1
      ? { source: { eq: list[0] } }
      : { or: list.map((source) => ({ source: { eq: source } })) }

  const items = await query({
    query: 'logsByPartner',
    variables: {
      partner_id: partnerId,
      createdAt: { ge: new Date(now - windowMs).toISOString() },
      filter: { event_name: { eq: eventName }, ...sourceFilter },
      limit: 1000
    }
  })

  return Array.isArray(items) && items.length > 0
}

/**
 * Schreibt den Log, den hasRecentPartnerLog findet. ttl löscht den Eintrag
 * nach dem Fenster, der TTL-Schalter der Log-Tabelle ist auf allen Stages
 * an. Ohne Personendaten, fired_by_id ist die Cognito-ID.
 *
 * @param {object} params
 * @param {string} params.partnerId
 * @param {string} params.eventName
 * @param {string} params.source Dedupe-Key
 * @param {string|null} [params.paymentId]
 * @param {string|null} [params.firedById]
 * @param {object} params.data geht als JSON in data
 * @param {number} params.windowMs
 * @param {number} params.now
 */
export const writePartnerLog = ({
  partnerId,
  eventName,
  source,
  paymentId = null,
  firedById = null,
  data,
  windowMs,
  now
}) =>
  mutation({
    mutation: 'createLog',
    input: {
      type: 'INFO',
      event_name: eventName,
      source,
      partner_id: partnerId,
      payment_id: paymentId || undefined,
      fired_by_id: firedById || undefined,
      data: JSON.stringify(data),
      ttl: Math.floor((now + windowMs) / 1000),
      authorized_create: [`__create_${partnerId}`],
      authorized_read: [`__read_low_${partnerId}`]
    },
    options: { disableLastUpdatedBy: true }
  })

// Gibt es am Partner in den letzten 24 h schon eine Anfrage mit diesem Key?
const hasRecentRequestLog = ({ partnerId, key, now }) =>
  hasRecentPartnerLog({
    partnerId,
    eventName: PRICING_REQUEST_LOG_EVENT,
    sources: key,
    windowMs: PRICING_REQUEST_COOLDOWN_MS,
    now
  })

const writeRequestLog = ({ descriptor, key, now }) =>
  writePartnerLog({
    partnerId: descriptor.partnerId,
    eventName: PRICING_REQUEST_LOG_EVENT,
    source: key,
    paymentId: descriptor.paymentId,
    firedById: descriptor.requester?.username,
    data: {
      kind: descriptor.kind,
      key: descriptor.key,
      target_plan: descriptor.targetPlan,
      source: descriptor.source
    },
    windowMs: PRICING_REQUEST_COOLDOWN_MS,
    now
  })

// ------------------------------------------------------------------ Versand

/**
 * sendSlackMessage wirft nur, wenn fetch scheitert. Slack lehnt aber auch mit
 * einer Antwort ab, 404 bei widerrufenem Webhook, 400 bei kaputtem Block-Kit,
 * 429 bei Rate Limit. Das ist hier ein Fehler, sonst sähe der Kunde
 * „Anfrage ist bei uns", und Cooldown und Log blockten 24 h lang einen neuen
 * Versuch. Ohne Response (development loggt nur) gilt der Versand als
 * erfolgt. Auch für die Nutzungs-Alerts in usage-alerts.js.
 *
 * @param {Response|undefined} response
 * @param {string} [label] Präfix der Fehlermeldung
 */
export const assertSlackAccepted = (response, label = 'pricing request') => {
  if (!response || response.ok !== false) return

  const error = new Error(`${label}: Slack antwortet mit ${response.status}`)
  notifyBugsnag(error)
  throw error
}

/**
 * Schickt die Anfrage nach #vertrieb, einmal pro Kunde und Gegenstand in
 * 24 h.
 *
 * Vor dem Senden markieren, so löst ein Doppelklick oder ein zweiter Tab
 * keine zweite Nachricht aus. Schlägt der Versand fehl, wird die Markierung
 * entfernt, damit „Erneut versuchen" sofort geht.
 *
 * @param {object} descriptor aus describePricingRequest
 * @param {{ now?: number, isAdmin?: boolean, canWriteLog?: boolean }} [options]
 *   canWriteLog heißt, die Person darf Logs am Partner anlegen
 * @returns {Promise<'SENT'|'DUPLICATE'|'DRY_RUN'>} wirft bei einem Fehler
 *   des Webhooks, auch wenn Slack mit einem Status außerhalb von 2xx
 *   antwortet
 */
export const maybeSendPricingRequest = async (
  descriptor,
  { now = Date.now(), isAdmin = false, canWriteLog = false } = {}
) => {
  if (!descriptor?.partnerId) {
    throw new Error('pricing request: partnerId fehlt')
  }

  if (isPricingRequestDryRun({ isAdmin, isSimulated: descriptor.isSimulated }))
    return PRICING_REQUEST_RESULTS.DRY_RUN

  const key = pricingRequestKey(descriptor)

  if (cooldown.wasSentRecently(key, now)) {
    return PRICING_REQUEST_RESULTS.DUPLICATE
  }

  cooldown.markSent(key, now)

  let requestedByTeam = false
  try {
    requestedByTeam = await hasRecentRequestLog({
      partnerId: descriptor.partnerId,
      key,
      now
    })
  } catch {
    // fail-open, die Dedupe pro Browser greift weiter
  }

  if (requestedByTeam) return PRICING_REQUEST_RESULTS.DUPLICATE

  try {
    const response = await sendSlackMessage(
      'vertrieb',
      buildPricingRequestPayload(descriptor, { isAdmin }),
      true
    )
    assertSlackAccepted(response)
  } catch (err) {
    cooldown.unmark(key, now)
    throw err
  }

  if (canWriteLog) {
    try {
      await writeRequestLog({ descriptor, key, now })
    } catch {
      // Ohne Log bleibt nur die Dedupe pro Browser, gesendet ist trotzdem
    }
  }

  return PRICING_REQUEST_RESULTS.SENT
}
